'use client';
import { useState } from 'react';
import { fromGid } from '@/lib/shopify/gid';
import { saveForLater } from '@/components/integrations/Actions';
import CTAButton from '@/components/elements/CTAButton';

const SaveForLaterModal = ({ productData, selectedVariant }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  // BL-465: a real submit handler, so the browser runs its own `type="email"`
  // check before this is ever called. The fields used to sit in a bare div with
  // an onClick button, and constraint validation only runs on form submission —
  // so nothing was validating anything and onii@gmail......com went through.
  //
  // Deliberately no regex here: the footer's SubscribeForm has one and it
  // accepts that exact address. What actually rejects it there is the browser,
  // for the same reason it will here.
  const submitSaveForLater = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const location = typeof window !== 'undefined' ? window.location : undefined;
      const shopifyProduct = productData?.shopifyProduct;
      const variantOptions = (selectedVariant?.selectedOptions ?? []).map(
        ({ name, value }) => `${name}: ${value}`,
      );

      const saveForLaterPayload = {
        hostname: location?.hostname,
        title: shopifyProduct?.title,
        page: location?.href,
        saved_mattress_variants: variantOptions,
        saved_mattress_product_id:
          productData?.shopify_product_id ?? fromGid(shopifyProduct?.id),
        saved_mattress_product_shopify_id: fromGid(shopifyProduct?.id),
      };

      const saveForLaterResponse = await saveForLater(email, saveForLaterPayload);
      setResponse(saveForLaterResponse);
    } catch {
      setResponse({
        success: false,
        error: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 md:px-5 py-10 md:py-23 max-w-xl mx-auto">
      <div className="font-cormorant text-primary text-center text-4xl lg:text-5xl font-semibold mb-4">
        Save this mattress for later!
      </div>

      <p className="font-worksans text-primary text-center text-lg md:text-2xl mb-6 font-normal">
        Save this mattress so you can pick right up where you left off!
      </p>

      <form
        onSubmit={submitSaveForLater}
        className="flex w-full flex-col items-stretch gap-4 md:w-3/4"
      >
        <input
          id="save-for-later"
          type="email"
          value={email}
          required
          className="font-worksans font-medium text-primary placeholder:text-primary/60 focus:ring-2 focus:ring-sea focus:outline-none w-full min-w-0 border border-sand focus:border-none pl-8 py-4 pr-3 rounded-full text-xl"
          placeholder="Enter your email"
          aria-label="Email address"
          autoComplete="email"
          onChange={(event) => setEmail(event.target.value)}
        />
        <CTAButton
          type="submit"
          disabled={isLoading}
          className="w-full px-8 py-4 font-worksans text-xl"
        >
          {isLoading ? 'Saving…' : 'Save Mattress'}
        </CTAButton>
      </form>

      {response && response.success && (
        <div className="font-bold text-primary">
          Successfully saved your mattress!
        </div>
      )}

      {response && !response.success && (
        <div className="font-bold text-dark-red">{response.error}</div>
      )}
    </div>
  );
};

export default SaveForLaterModal;
